import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const pillars = [
  { key: "areas" },
  { key: "data" },
  { key: "control" },
];

export function SolutionsSection() {
  const { t } = useTranslation();

  return (
    <section className="py-12 lg:py-32 bg-primary text-primary-foreground relative overflow-hidden min-h-[300px]">
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-10 left-10 w-64 h-64 rounded-full border border-primary-foreground/5"
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.3 }}
        />
        <motion.div
          className="absolute bottom-10 right-10 w-48 h-48 rounded-full border border-primary-foreground/5"
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.5 }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-primary-foreground/3"
          initial={{ scale: 0.5, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, delay: 0.1 }}
        />
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-10 lg:mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-medium font-heading tracking-tight">
            {t("solutions.title")}
          </h2>
          <p className="mt-4 text-lg text-primary-foreground/70 font-body">
            {t("solutions.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0 items-start max-w-4xl mx-auto">
          {pillars.map((pillar, index) => (
            <div key={pillar.key} className="flex items-start">
              <motion.div
                className="flex-1 text-center px-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                {/* Numbered circle */}
                <motion.div
                  className="w-12 h-12 lg:w-16 lg:h-16 rounded-full bg-coral flex items-center justify-center mx-auto"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    type: "spring",
                    stiffness: 250,
                    damping: 15,
                    delay: 0.3 + index * 0.2,
                  }}
                >
                  <span className="text-2xl font-bold text-coral-foreground font-heading">
                    {index + 1}
                  </span>
                </motion.div>

                <h3 className="text-xl font-bold font-subheading mt-6">
                  {t(`solutions.pillars.${pillar.key}.title`)}
                </h3>
                <p className="text-primary-foreground/70 font-body leading-relaxed mt-3 text-sm">
                  {t(`solutions.pillars.${pillar.key}.description`)}
                </p>
              </motion.div>

              {/* Arrow between steps (hidden on mobile and after last) */}
              {index < 2 && (
                <motion.div
                  className="hidden md:flex items-center pt-8 -mx-4"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 + index * 0.2, duration: 0.4 }}
                >
                  <ArrowRight className="h-6 w-6 text-coral/40" />
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
